/**
 * 中国象棋游戏组件
 * 完整的中国象棋游戏体验，支持人机对战和双人对战
 */

import React, { useState, useEffect, useCallback } from 'react';
import XiangqiBoard from './XiangqiBoard';
import GameShareButton from './GameShareButton';
import { GameMode, Difficulty, XiangqiGameState, XiangqiMove, Position } from '../types/game';
import { createInitialGameState, selectPiece, deselectPiece, makeMove, getAllPossibleMoves, undoMove } from '../utils/xiangqi';
import { getXiangqiAIDecision, simulateXiangqiAIThinking } from '../utils/xiangqiAI';

interface XiangqiGameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
}

const XiangqiGame: React.FC<XiangqiGameProps> = ({ gameMode, difficulty = 'medium', onBackToMenu, onBackToModeSelect }) => {
  const [gameState, setGameState] = useState<XiangqiGameState>(() =>
    createInitialGameState(gameMode, difficulty)
  );

  const [showWinDialog, setShowWinDialog] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [dialogManuallyClosed, setDialogManuallyClosed] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  // AI移动逻辑 - 优化版本
  const makeAIMove = useCallback(async () => {
    // 确保只有AI回合才执行
    if (gameMode !== 'pvc' || gameState.currentPlayer === 'red' || gameState.status !== 'playing' || isThinking) {
      return;
    }

    setIsThinking(true);

    try {
      // 获取AI决策（使用优化后的AI算法）
      const aiMove = getXiangqiAIDecision(gameState, difficulty);

      if (aiMove) {
        // 模拟AI思考时间（使用优化后的思考时间）
        await simulateXiangqiAIThinking(difficulty);

        // 执行移动
        setGameState(prevState => makeMove(prevState, aiMove));
        // 重置思考状态
        setIsThinking(false);
      } else {
        // 如果没有可用移动，直接切换回合
        console.warn('AI没有找到可用移动');
        setGameState(prevState => ({
          ...prevState,
          currentPlayer: 'red',
          status: 'finished',
          winner: 'red'
        }));
        // 重置思考状态
        setIsThinking(false);
      }
    } catch (error) {
      console.error('AI移动出错:', error);
      // 出错时重置思考状态
      setIsThinking(false);
    }
  }, [gameMode, difficulty, gameState, isThinking]);

  // 处理格子点击
  const handleCellClick = useCallback((position: Position) => {
    if (gameState.status !== 'playing' || isThinking) return;

    const piece = gameState.board[position.row][position.col];

    if (gameState.selectedPiece) {
      // 已经选中了棋子，尝试移动
      const selectedMove = gameState.possibleMoves?.find(
        move => move.to.row === position.row && move.to.col === position.col
      );

      if (selectedMove) {
        // 执行移动
        setGameState(prevState => makeMove(prevState, selectedMove));
      } else if (piece && piece.player === gameState.currentPlayer) {
        // 选择新的棋子
        setGameState(prevState => selectPiece(prevState, position));
      } else {
        // 取消选择
        setGameState(prevState => deselectPiece(prevState));
      }
    } else {
      // 还没有选中棋子，尝试选择
      if (piece && piece.player === gameState.currentPlayer) {
        setGameState(prevState => selectPiece(prevState, position));
      }
    }
  }, [gameState, isThinking]);

  // 检查游戏结束
  useEffect(() => {
    if (gameState.status === 'finished' && !showWinDialog && !dialogManuallyClosed) {
      setShowWinDialog(true);
    }
  }, [gameState.status, showWinDialog, dialogManuallyClosed]);

  // 更新悔棋按钮状态
  useEffect(() => {
    setCanUndo(gameState.gameHistory.length > 0 && gameState.status === 'playing');
  }, [gameState.gameHistory.length, gameState.status]);

  // AI回合处理 - 优化版本
  useEffect(() => {
    // 使用超时确保在下一个渲染周期执行AI移动
    const timeoutId = setTimeout(() => {
      if (gameMode === 'pvc' && gameState.currentPlayer === 'black' && gameState.status === 'playing' && !isThinking) {
        makeAIMove();
      }
    }, 100); // 100ms延迟确保状态稳定

    return () => clearTimeout(timeoutId);
  }, [gameState.currentPlayer, gameState.status, gameMode, makeAIMove, isThinking]);

  // 重新开始游戏
  const handleRestart = () => {
    setGameState(createInitialGameState(gameMode, difficulty));
    setShowWinDialog(false);
    setDialogManuallyClosed(false);
    setIsThinking(false);
    setCanUndo(false);
  };

  // 悔棋功能
  const handleUndo = () => {
    if (gameState.gameHistory.length > 0 && !isThinking && gameState.status === 'playing') {
      const newState = undoMove(gameState);
      setGameState(newState);
      setCanUndo(newState.gameHistory.length > 0);
    }
  };

  
  // 返回菜单
  const handleBackToMenu = () => {
    if (onBackToMenu) {
      onBackToMenu();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">🏮</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-red-600 via-maroon-600 to-purple-700 bg-clip-text text-transparent sm:mb-1">
                中国象棋
              </h1>
              <span className="text-2xl">♟️</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}

              {/* 放大模式提示 */}
              {isZoomed && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 animate-pulse">
                  <span className="w-2 h-2 mr-1 bg-green-500 rounded-full"></span>
                  🔍 放大模式
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            {onBackToMenu && (
              <button
                onClick={onBackToMenu}
                className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium"
                title="回到首页"
              >
                <span>🏠</span>
                <span className="hidden sm:inline">首页</span>
              </button>
            )}
            {/* 返回按钮 */}
            {(onBackToMenu || onBackToModeSelect) && (
              <button
                onClick={onBackToModeSelect || onBackToMenu}
                className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium"
                title="返回模式选择"
              >
                <span>←</span>
                <span className="hidden sm:inline">返回</span>
              </button>
            )}

            {/* 重新开始按钮 */}
            <button
              onClick={handleRestart}
              className="flex items-center space-x-1 px-2 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-xs font-medium"
              title="重新开始"
            >
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button
              onClick={handleUndo}
              disabled={!canUndo || isThinking}
              className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
                canUndo && !isThinking
                  ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title="悔棋"
            >
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 放大按钮 */}
            <button
              onClick={() => setIsZoomed(!isZoomed)}
              className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium"
              title={isZoomed ? "缩小棋盘" : "放大棋盘"}
            >
              <span>{isZoomed ? '🔍' : '🔎'}</span>
              <span className="hidden sm:inline">{isZoomed ? '缩小' : '放大'}</span>
            </button>
            <GameShareButton gameType="xiangqi" />
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      <div className="bg-white shadow p-2">
        <div className="flex items-center justify-between">
          {/* 当前玩家信息 */}
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded-full ${
              gameState.currentPlayer === 'black' ? 'bg-gray-900' : 'bg-red-600'
            }`}></div>
            <span className="text-sm sm:text-base font-medium">
              {gameState.status === 'playing' ? (
                isThinking && gameMode === 'pvc' && gameState.currentPlayer === 'black' ? (
                  <span className="text-blue-600">AI思考中...</span>
                ) : (
                  <span>{gameState.currentPlayer === 'black' ? '黑方' : '红方'}回合</span>
                )
              ) : (
                <span className="text-green-600">
                  {gameState.winner ? `${gameState.winner === 'black' ? '黑方' : '红方'}获胜！` : '游戏结束'}
                </span>
              )}
            </span>
            {gameState.isCheck && (
              <span className="bg-red-500 text-white px-2 py-1 rounded text-xs animate-pulse">
                将军！
              </span>
            )}
          </div>

          {/* 步数信息 */}
          <div className="flex items-center space-x-3 text-sm sm:text-base text-gray-600">
            <span>步数: {gameState.totalMoves}</span>
            <span>将军: {gameState.checkMoves}</span>
            {gameMode === 'pvc' && (
              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs">
                {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 棋盘 - 移动端满屏布局 */}
      <div className="flex-1 flex items-center justify-center px-1 py-1 xs:px-1 xs:py-2">
        <div className="w-full h-full max-w-none">
          <XiangqiBoard
            gameState={gameState}
            onCellClick={handleCellClick}
            disabled={isThinking || gameState.status !== 'playing'}
            gameMode={gameMode}
            isZoomed={isZoomed}
            onZoomToggle={() => setIsZoomed(!isZoomed)}
          />
        </div>
      </div>

      {/* 游戏结束对话框 */}
      {showWinDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 transform scale-100 relative">
            {/* 关闭按钮 */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowWinDialog(false);
                setDialogManuallyClosed(true);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>
            <div className="text-center">
              <div className="text-6xl mb-4">
                {gameState.winner === 'red' ? '🎉' : '🏆'}
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {gameState.winner === 'red' ? '红方胜利！' :
                 gameState.winner === 'black' ? '黑方胜利！' : '和棋！'}
              </h2>
              <p className="text-gray-600 mb-6">
                {gameState.isCheckmate ? '将死获胜' :
                 gameState.isStalemate ? '和棋' : '游戏结束'}
              </p>

              {/* 游戏统计 */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">总步数:</span>
                    <span className="ml-1">{gameState.totalMoves}</span>
                  </div>
                  <div>
                    <span className="font-semibold">吃子数:</span>
                    <span className="ml-1">{gameState.captureMoves}</span>
                  </div>
                  <div>
                    <span className="font-semibold">将军次数:</span>
                    <span className="ml-1">{gameState.checkMoves}</span>
                  </div>
                  <div>
                    <span className="font-semibold">游戏模式:</span>
                    <span className="ml-1">{gameMode === 'pvp' ? '双人对战' : '人机对战'}</span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4 justify-center">
                {gameState.gameHistory.length > 0 && (
                  <button
                    onClick={() => {
                      handleUndo();
                      setShowWinDialog(false);
                    }}
                    className="px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-semibold"
                  >
                    悔棋继续
                  </button>
                )}
                <button
                  onClick={handleRestart}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold"
                >
                  再来一局
                </button>
                <button
                  onClick={handleBackToMenu}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-semibold"
                >
                  返回菜单
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 游戏规则提示 - 统一布局 */}
      <div className="bg-amber-50 border-t border-amber-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-amber-800 text-center leading-relaxed">
          <strong>规则：</strong>
          中国象棋 • 楚河汉界 • 红黑对弈 |
          {gameMode === 'pvp' ? '双人对战' : '人机对战'} |
          将/帅九宫内移动 | 车/炮直线移动 | 马走日字 | 兵卒过河可横移
        </p>
      </div>
    </div>
  );
};

export default XiangqiGame;